import Dexie, { type Table } from 'dexie';

// Sync metadata attached to every offline record
export interface SyncMeta {
  _syncStatus: 'synced' | 'pending_create' | 'pending_update' | 'pending_delete';
  _lastModified: number; // epoch ms
}

export interface DexieProfile extends SyncMeta {
  user_id: string;
  display_name: string | null;
  business_name: string | null;
  email: string | null;
  default_language: string;
  pin_code: string | null;
  pin_enabled: boolean;
  logo_url: string | null;
}

export interface DexieTemplate extends SyncMeta {
  id: string;
  owner_id: string;
  name: Record<string, string>;
  category: string;
  description: Record<string, string>;
  icon: string;
  sections: any[];
  fields: any[];
  checkboxes: any[];
  tags: string[];
  archived: boolean;
  is_system?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DexieSignedContract extends SyncMeta {
  id: string;
  owner_id: string;
  template_id: string | null;
  template_name: string;
  client_id: string | null;
  client_name: string;
  client_email: string | null;
  language: string;
  field_values: Record<string, string>;
  checkbox_values: Record<string, boolean>;
  signature_data_url: string | null;
  signed_at: string;
  practitioner_email: string | null;
}

export interface DexieDraft {
  id: string;
  owner_id: string;
  template_id: string;
  template_name: string;
  client_id: string | null;
  client_name: string;
  field_values: Record<string, string>;
  checkbox_values: Record<string, boolean>;
  step: number;
  language: string;
  created_at: string;
  updated_at: string;
}

class ConsentinkDB extends Dexie {
  profiles!: Table<DexieProfile, string>;
  templates!: Table<DexieTemplate, string>;
  signedContracts!: Table<DexieSignedContract, string>;
  drafts!: Table<DexieDraft, string>;

  constructor() {
    super('ConsentinkDB');

    this.version(1).stores({
      profiles: 'user_id',
      templates: 'id, owner_id, _syncStatus',
      signedContracts: 'id, owner_id, _syncStatus',
    });

    this.version(2).stores({
      profiles: 'user_id',
      templates: 'id, owner_id, _syncStatus',
      signedContracts: 'id, owner_id, client_id, _syncStatus',
    });

    // v3: force re-pull from Supabase to populate client_id on existing contracts
    this.version(3).stores({
      profiles: 'user_id',
      templates: 'id, owner_id, _syncStatus',
      signedContracts: 'id, owner_id, client_id, _syncStatus',
    }).upgrade(tx => {
      return tx.table('signedContracts')
        .filter(c => c._syncStatus === 'synced')
        .delete();
    });

    // v4: remove default templates with fixed UUIDs
    this.version(4).stores({
      profiles: 'user_id',
      templates: 'id, owner_id, _syncStatus',
      signedContracts: 'id, owner_id, client_id, _syncStatus',
    }).upgrade(tx => {
      const fixedIds = [
        'a1b2c3d4-e5f6-7890-abcd-ef1234567001',
        'a1b2c3d4-e5f6-7890-abcd-ef1234567002',
        'a1b2c3d4-e5f6-7890-abcd-ef1234567003',
        'a1b2c3d4-e5f6-7890-abcd-ef1234567004',
        'a1b2c3d4-e5f6-7890-abcd-ef1234567005',
      ];
      return tx.table('templates')
        .filter(t => fixedIds.includes(t.id))
        .delete();
    });

    // v5: add drafts table
    this.version(5).stores({
      profiles: 'user_id',
      templates: 'id, owner_id, _syncStatus',
      signedContracts: 'id, owner_id, client_id, _syncStatus',
      drafts: 'id, owner_id, template_id, client_id, updated_at',
    });

    // v6: logo_url added to profiles (schema unchanged — Dexie doesn't index it)
    this.version(6).stores({
      profiles: 'user_id',
      templates: 'id, owner_id, _syncStatus',
      signedContracts: 'id, owner_id, client_id, _syncStatus',
      drafts: 'id, owner_id, template_id, client_id, updated_at',
    });
  }
}

export const db = new ConsentinkDB();
